import { CategoryIds } from '../../CategoryTabs'
import { ProgressData, MappedProgressData, Service } from '../types'

type CalculatedProgressParams = {
  progress: number
  scoreType: 'count' | 'percentage'
  targetScore: number
}

type GenerateNextActionUrlParams = {
  widgets: Service[]
  currentWidgetIndex: number
  categories: CategoryIds[]
  currentCategoryIndex: number
  serviceData: MappedProgressData
}

type MapProgressData = (
  progressData?: ProgressData,
  serviceData?: MappedProgressData,
) => MappedProgressData

const calculateProgress = ({ progress, scoreType, targetScore }: CalculatedProgressParams) => {
  const percentage = (progress / targetScore) * 100
  const percentageProgress = scoreType === 'percentage' ? progress : percentage
  const formattedProgress = scoreType === 'percentage' ? `${progress}%` : progress

  return { formattedProgress, percentageProgress }
}

const generateNextActionUrl = ({
  widgets,
  currentWidgetIndex,
  categories,
  currentCategoryIndex,
  serviceData,
}: GenerateNextActionUrlParams): string => {
  const nextWidget = widgets[currentWidgetIndex + 1]

  if (nextWidget) {
    return `?categoryTab=${categories[currentCategoryIndex]}&serviceId=${nextWidget.id}`
  }

  // Move to next category or wrap to first
  const nextCategoryIndex = currentCategoryIndex + 1
  const nextCategory = categories[nextCategoryIndex] || categories[0]
  const nextCategoryWidget = serviceData[nextCategory][0]?.id || ''

  return `?categoryTab=${nextCategory}&serviceId=${nextCategoryWidget}`
}

const filterWidget = (widgetData?: ProgressData) => (widget: Service) => {
  if (widget.id === 'resume_tailoring' && widgetData?.[widget.id]?.has_expert_resumes) {
    return false
  }

  return widget.enabled
}

export const mapProgressData: MapProgressData = (progressData, serviceData) => {
  const mappedProgressData: MappedProgressData = {
    resumes: [],
    jobs: [],
    interview: [],
    learning: [],
  }

  if (!serviceData) return mappedProgressData
  const categories = Object.keys(serviceData) as CategoryIds[]

  for (const category of categories) {
    const widgets = serviceData[category] || []
    const categoryIndex = categories.indexOf(category)
    const enabledWidgets = widgets.filter(filterWidget(progressData))

    for (const widget of enabledWidgets) {
      const widgetIndex = enabledWidgets.indexOf(widget)
      const nextActionUrl = generateNextActionUrl({
        widgets: enabledWidgets,
        currentWidgetIndex: widgetIndex,
        categories,
        currentCategoryIndex: categoryIndex,
        serviceData,
      })

      const progressDataItem = progressData?.[widget.id as keyof ProgressData]

      mappedProgressData[category].push({
        ...widget,
        data: progressDataItem || {},
        widgetTipOrder: widgetIndex,
        getNextActionUrl: () => nextActionUrl,
        progress: progressDataItem?.progress || 0,
        calculatedProgress: calculateProgress({
          progress: progressDataItem?.progress || 0,
          scoreType: progressDataItem?.score_type || 'percentage',
          targetScore: progressDataItem?.target_score || 0,
        }),
      })
    }
  }

  return mappedProgressData
}
